/**
 * Mock JobSalaryClient for demo mode
 *
 * This client simulates the real JobSalaryClient without requiring NATS authentication.
 * It provides the same interface and includes simulated real-time updates using intervals.
 */
import type { JobSalaryClient } from "./JobSalary.client";
import type { HistoricSalary } from "./JobSalary.dto";
import {
  MOCK_JOB_SALARIES,
  MOCK_METRO_AREAS,
  MOCK_QUANTILES,
  MOCK_RELATED_CAREERS,
  MOCK_WATCHLIST_GROUPS,
  UNKNOWN_JOB_SALARY,
  createMockJobSalary,
} from "./JobSalary.mock";
import type { JobSalary } from "./JobSalary.model";
import type { MetroArea, Quantile } from "./watchlist.filters";

/**
 * Generate 5-year historical salary data for a given current salary
 * Creates realistic growth patterns with some volatility
 */
function generateHistoricalSalaryData(currentSalary: number, timePeriod: "ytd" | "five-year"): HistoricSalary[] {
  const data: HistoricSalary[] = [];
  const now = new Date();
  const monthsBack = timePeriod === "five-year" ? 60 : 12;
  const startDate = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);

  // Calculate starting salary (5 years ago should be ~15-20% lower)
  const annualGrowthRate = 0.03; // ~3% annual growth
  const yearsBack = monthsBack / 12;
  let currentMedian = currentSalary / (1 + annualGrowthRate) ** yearsBack;

  for (let i = 0; i < monthsBack; i++) {
    // Calculate date properly to avoid month overflow issues
    const date = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);

    // Add monthly growth with some volatility
    const monthlyGrowth = annualGrowthRate / 12; // ~0.25% per month
    const volatility = (Math.random() - 0.5) * 0.02; // ±1% monthly volatility
    currentMedian *= 1 + monthlyGrowth + volatility;

    // Calculate percentile values (typical distribution)
    const pct10 = currentMedian * 0.75; // 10th percentile is ~75% of median
    const pct25 = currentMedian * 0.88; // 25th percentile is ~88% of median
    const median = currentMedian;
    const pct75 = currentMedian * 1.15; // 75th percentile is ~115% of median
    const pct90 = currentMedian * 1.35; // 90th percentile is ~135% of median

    data.push({
      time: date.getTime(), // Timestamp in milliseconds
      salary: Math.round(median), // For backwards compatibility
      a_pct10: Math.round(pct10),
      a_pct25: Math.round(pct25),
      a_median: Math.round(median),
      a_pct75: Math.round(pct75),
      a_pct90: Math.round(pct90),
    });
  }

  return data;
}

export class MockJobSalaryClient implements JobSalaryClient {
  // Store created JobSalary instances
  public jobSalaries: Map<string, JobSalary> = new Map();

  // Map job subjects to titles (matching real client interface)
  public jobSubjectToTitle: Map<string, string> = new Map();

  // Interval for simulating real-time updates
  private updateInterval: NodeJS.Timeout | null = null;

  // Track pending flash reset timeouts per JobSalary to prevent overlapping resets
  private flashResetTimeouts: WeakMap<JobSalary, NodeJS.Timeout[]> = new WeakMap();

  // Flag to track if client has been started
  private hasStarted = false;

  // Store static data as instance properties for interface compatibility
  private readonly watchlistGroups = MOCK_WATCHLIST_GROUPS;

  private readonly metroAreas = MOCK_METRO_AREAS;

  private readonly quantiles = MOCK_QUANTILES;

  /**
   * Static factory method to create a mock client instance
   * Mirrors the real JobSalaryClient.create() signature
   */
  public static create(): MockJobSalaryClient {
    const client = new MockJobSalaryClient();
    client.start();
    return client;
  }

  /**
   * Initialize the mock client and start simulation
   */
  private start(): void {
    if (this.hasStarted) {
      return;
    }

    // Mark as started
    this.hasStarted = true;

    // Initialize jobSubjectToTitle map from watchlist entries
    this.initializeJobSubjectToTitle();

    // Pre-create some common JobSalary instances for quick access
    this.preloadJobSalaries();

    // Start simulating real-time updates
    this.startRealtimeSimulation();
  }

  /**
   * Get all watchlist groups
   */
  public getWatchlistGroups() {
    return this.watchlistGroups;
  }

  /**
   * Get all metro areas
   */
  public getMetroAreas(): MetroArea[] {
    return this.metroAreas;
  }

  /**
   * Get all quantiles (experience levels)
   */
  public getQuantiles(): Quantile[] {
    return this.quantiles;
  }

  /**
   * Get job salaries for a specific watchlist, metro area, and quantile
   */
  public getJobSalaries(
    watchlistId: string,
    metroAreaId: string,
    quantileId: string,
  ): { watchlist: any; jobSalaries: JobSalary[] } | null {
    // Find the watchlist
    let watchlist = null;
    for (const group of this.watchlistGroups) {
      watchlist = group.watchlists.find((w) => w.id === watchlistId);
      if (watchlist) break;
    }

    if (!watchlist) {
      return null;
    }

    const metroArea = this.metroAreas.find((m) => m.id === metroAreaId);
    if (!metroArea) {
      return null;
    }

    const quantile = this.quantiles.find((q) => q.id === quantileId);
    if (!quantile) {
      return null;
    }

    // Create or get JobSalary instances for all entries
    const jobSalaries = watchlist.entries
      .map((entry) => {
        const mockData = MOCK_JOB_SALARIES.find((j) => j.jobSubject === entry.jobSubject);
        if (!mockData) {
          return null;
        }
        return this.getOrCreateJobSalary(entry.jobSubject, metroArea, quantile);
      })
      .filter((js): js is JobSalary => js !== null);

    return { watchlist, jobSalaries };
  }

  /**
   * Get or create a JobSalary instance
   */
  public getOrCreateJobSalary(jobSubject: string, metroArea: MetroArea, quantile: Quantile): JobSalary {
    const id = `job-salary.${jobSubject}.${metroArea.id}.${quantile.id}`;

    // Return existing instance if available
    const existing = this.jobSalaries.get(id);
    if (existing) {
      return existing;
    }

    // Find mock data
    const mockData = MOCK_JOB_SALARIES.find((j) => j.jobSubject === jobSubject);

    // Create new instance
    const jobSalary = createMockJobSalary(mockData ?? UNKNOWN_JOB_SALARY, metroArea, quantile);
    this.jobSalaries.set(id, jobSalary);

    return jobSalary;
  }

  /**
   * Get related job salaries for a given JobSalary
   */
  public getRelatedJobSalaries(jobSalary: JobSalary): JobSalary[] {
    const relatedSubjects = MOCK_RELATED_CAREERS[jobSalary.streamName] || [];

    return relatedSubjects.map((subject) =>
      this.getOrCreateJobSalary(subject, jobSalary.metroArea, jobSalary.quantile),
    );
  }

  /**
   * Get job salaries for all quantiles (experience levels) for a specific job
   */
  public getJobSalariesForAllQuantiles(jobSalaryId: string): JobSalary[] {
    const parts = jobSalaryId.split(".");
    if (parts.length !== 4 || parts[0] !== "job-salary") {
      return [];
    }

    const jobSubject = parts[1];
    const metroAreaId = parts[2];

    const metroArea = this.metroAreas.find((m) => m.id === metroAreaId);
    if (!metroArea) {
      return [];
    }

    return this.quantiles.map((quantile) => {
      return this.getOrCreateJobSalary(jobSubject, metroArea, quantile);
    });
  }

  /**
   * Fetch historic salary data - generates realistic 5-year historical data
   */
  // eslint-disable-next-line class-methods-use-this
  public async fetchHistoricSalaryData(jobSalary: JobSalary, type: "ytd" | "five-year"): Promise<HistoricSalary[]> {
    // Get current salary value
    const currentSalary = jobSalary.salary.value;

    // If salary is not available, return empty array
    if (!currentSalary || Number.isNaN(currentSalary)) {
      return [];
    }

    // Generate historical data based on current salary
    return generateHistoricalSalaryData(currentSalary, type);
  }

  /**
   * Initialize jobSubjectToTitle map from watchlist entries
   */
  private initializeJobSubjectToTitle(): void {
    this.watchlistGroups.forEach((group) => {
      group.watchlists.forEach((watchlist) => {
        watchlist.entries.forEach((entry) => {
          this.jobSubjectToTitle.set(entry.jobSubject, entry.jobTitle);
        });
      });
    });
  }

  /**
   * Pre-load some common job salaries for quick initial rendering
   */
  private preloadJobSalaries(): void {
    const defaultMetroArea = this.metroAreas[0]; // Chicago
    const defaultQuantile = this.quantiles[2]; // 5-10y exp (median)

    // Pre-create JobSalary instances for Tom's Picks
    const watchlist = this.watchlistGroups[0].watchlists[0];
    watchlist.entries.forEach((entry) => {
      this.getOrCreateJobSalary(entry.jobSubject, defaultMetroArea, defaultQuantile);
    });
  }

  /**
   * Start simulating real-time updates to JobSalary signals
   */
  private startRealtimeSimulation(): void {
    if (this.updateInterval) {
      return;
    }

    // Update every 1 second
    this.updateInterval = setInterval(() => {
      this.simulateUpdate();
    }, 3000);
  }

  /**
   * Simulate a single update cycle
   * Randomly picks 10-15 job salaries and updates their values
   */
  private simulateUpdate(): void {
    const allJobSalaries = Array.from(this.jobSalaries.values());

    if (allJobSalaries.length === 0) {
      return;
    }

    // Pick 10-15 random job salaries to update
    const numToUpdate = Math.floor(Math.random() * 6) + 10; // 10-15
    const shuffled = [...allJobSalaries].sort(() => Math.random() - 0.5);
    const toUpdate = shuffled.slice(0, Math.min(numToUpdate, allJobSalaries.length));

    toUpdate.forEach((jobSalary) => {
      this.updateJobSalary(jobSalary);
    });
  }

  /**
   * Clear any pending flash reset timeouts for a JobSalary
   */
  private clearFlashResetTimeouts(jobSalary: JobSalary): void {
    const timeouts = this.flashResetTimeouts.get(jobSalary);
    if (timeouts) {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      this.flashResetTimeouts.delete(jobSalary);
    }
  }

  /**
   * Schedule a flash reset timeout for a JobSalary
   */
  private scheduleFlashReset(jobSalary: JobSalary, resetFn: () => void): void {
    const timeout = setTimeout(() => {
      resetFn();
      const timeouts = this.flashResetTimeouts.get(jobSalary);
      if (timeouts) {
        const index = timeouts.indexOf(timeout);
        if (index > -1) {
          timeouts.splice(index, 1);
        }
        if (timeouts.length === 0) {
          this.flashResetTimeouts.delete(jobSalary);
        }
      }
    }, 1000);

    const existingTimeouts = this.flashResetTimeouts.get(jobSalary) || [];
    existingTimeouts.push(timeout);
    this.flashResetTimeouts.set(jobSalary, existingTimeouts);
  }

  /**
   * Update a single JobSalary with new simulated values
   *
   * This method simulates real-time updates by:
   * 1. Calculating a small random change to the salary
   * 2. Updating salaryUp/salaryDown flags based on immediate change (newSalary vs currentSalary)
   * 3. Updating cumulative netChange and its up/down flags
   * 4. Occasionally updating growthRate and its up/down flags
   *
   * Note: The flash animation reset/set pattern (clearing flags then setting in setTimeout)
   * is intentional to trigger CSS animations when values change.
   */
  private updateJobSalary(jobSalary: JobSalary): void {
    // Verify jobSalary is tracked in this instance
    const isTracked = Array.from(this.jobSalaries.values()).includes(jobSalary);
    if (!isTracked) {
      return;
    }

    // Store current salary value before calculating change
    const currentSalary = jobSalary.salary.value;

    // Generate an extremely small change (±0.002% to ±0.02%)
    // Ensure change is always non-zero by using a minimum absolute value
    const changePercent = (Math.random() * 0.00018 + 0.00002) * (Math.random() < 0.5 ? -1 : 1);
    let newSalary = Math.round(currentSalary * (1 + changePercent));
    let change = newSalary - currentSalary;

    // If change rounds to 0, force it to ±1 to ensure there's always a visible change
    if (change === 0) {
      change = Math.random() < 0.5 ? -1 : 1;
      newSalary = currentSalary + change;
    }

    // Update salary value
    // eslint-disable-next-line no-param-reassign
    jobSalary.salary.value = newSalary;

    // Update salaryUp/salaryDown flags based on immediate change
    // These flags reflect whether the new salary is higher/lower than the previous salary
    // (matches real implementation pattern where backend sends pre-computed flags)
    const shouldBeUp = change > 0; // newSalary > currentSalary
    const shouldBeDown = change < 0; // newSalary < currentSalary

    // Only update if state is changing (to trigger animation)
    if (jobSalary.salaryUp.value !== shouldBeUp || jobSalary.salaryDown.value !== shouldBeDown) {
      // Clear previous flash state first to ensure animation retriggers
      // This reset/set pattern is intentional to trigger CSS flash animations
      // eslint-disable-next-line no-param-reassign
      jobSalary.salaryUp.value = false;
      // eslint-disable-next-line no-param-reassign
      jobSalary.salaryDown.value = false;

      // Set flash state in next tick to trigger animation
      setTimeout(() => {
        // eslint-disable-next-line no-param-reassign
        jobSalary.salaryUp.value = shouldBeUp;
        // eslint-disable-next-line no-param-reassign
        jobSalary.salaryDown.value = shouldBeDown;

        // No reset needed - flash animation runs once and signals persist
      }, 0);
    }

    // Update cumulative netChange
    // netChange accumulates all changes over time (not reset between updates)
    const currentNetChange = jobSalary.netChange.value;
    const newNetChange = currentNetChange + change;

    // eslint-disable-next-line no-param-reassign
    jobSalary.netChange.value = newNetChange;

    // Update netChangeUp/netChangeDown flags based on cumulative netChange value
    // These flags reflect whether the cumulative netChange is positive or negative
    // (matches real implementation pattern where backend sends pre-computed flags)
    const shouldBeNetChangeUp = newNetChange > 0;
    const shouldBeNetChangeDown = newNetChange < 0;

    // Only update if state is changing (to trigger animation)
    if (
      jobSalary.netChangeUp.value !== shouldBeNetChangeUp ||
      jobSalary.netChangeDown.value !== shouldBeNetChangeDown
    ) {
      // Clear previous flash state first to ensure animation retriggers
      // This reset/set pattern is intentional to trigger CSS flash animations
      // eslint-disable-next-line no-param-reassign
      jobSalary.netChangeUp.value = false;
      // eslint-disable-next-line no-param-reassign
      jobSalary.netChangeDown.value = false;

      // Set flash state in next tick to trigger animation
      setTimeout(() => {
        // eslint-disable-next-line no-param-reassign
        jobSalary.netChangeUp.value = shouldBeNetChangeUp;
        // eslint-disable-next-line no-param-reassign
        jobSalary.netChangeDown.value = shouldBeNetChangeDown;

        // No reset needed - signals persist based on actual value, flash animation runs once
      }, 0);
    }

    // Occasionally update growth rate (30% chance per update cycle)
    if (Math.random() < 0.3) {
      const currentGrowthRate = jobSalary.growthRate.value;
      const growthChange = Math.random() * 0.00008 - 0.00004; // ±0.004%
      const newGrowthRate = currentGrowthRate + growthChange;

      // eslint-disable-next-line no-param-reassign
      jobSalary.growthRate.value = newGrowthRate;

      // Update growthRateUp/growthRateDown flags based on growthRate sign
      // (matches real implementation pattern: growthRateUp = growthRate > 0)
      const shouldBeGrowthRateUp = newGrowthRate > 0;
      const shouldBeGrowthRateDown = newGrowthRate < 0;

      // Only update if state is changing (to trigger animation)
      if (
        jobSalary.growthRateUp.value !== shouldBeGrowthRateUp ||
        jobSalary.growthRateDown.value !== shouldBeGrowthRateDown
      ) {
        // Clear previous flash state first to ensure animation retriggers
        // This reset/set pattern is intentional to trigger CSS flash animations
        // eslint-disable-next-line no-param-reassign
        jobSalary.growthRateUp.value = false;
        // eslint-disable-next-line no-param-reassign
        jobSalary.growthRateDown.value = false;

        // Set flash state in next tick to trigger animation
        setTimeout(() => {
          // eslint-disable-next-line no-param-reassign
          jobSalary.growthRateUp.value = shouldBeGrowthRateUp;
          // eslint-disable-next-line no-param-reassign
          jobSalary.growthRateDown.value = shouldBeGrowthRateDown;

          // No reset needed - signals persist based on actual value, flash animation runs once
        }, 0);
      }
    }

    // Update projections based on new salary
    // eslint-disable-next-line no-param-reassign
    jobSalary.totalCostProjectionOneYear.value = newSalary * 1.03;
    // eslint-disable-next-line no-param-reassign
    jobSalary.totalCostProjectionThreeYears.value = newSalary * 1.09;
    // eslint-disable-next-line no-param-reassign
    jobSalary.totalCostProjectionFiveYears.value = newSalary * 1.15;
  }

  /**
   * Stop the real-time simulation and cleanup
   */
  public destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    // Clear all job salary instances
    this.jobSalaries.clear();
    this.hasStarted = false;
  }
}
